import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  input,
  Input,
  OnDestroy,
  OnInit,
  Output,
  signal,
  ViewChild,
} from "@angular/core";
import { AbstractControl, FormArray, FormBuilder, FormGroup, FormControl } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";

import { AgGridAngular } from "ag-grid-angular";
import {
  ColDef,
  GridApi,
  GridOptions,
  GridReadyEvent,
  ProcessDataFromClipboardParams,
  RowDragEndEvent,
  RowDragEnterEvent,
  ValueParserParams,
  ValueSetterParams,
} from "ag-grid-enterprise";
import { lastValueFrom, Subscription } from "rxjs";

import { ConfirmDialogComponent, EditAreaDialogComponent, TableUtils } from "@components/index";
import { TableTooltip } from "@components/shared/tables/table/table-tooltip";
import { IconConstants, TextConstants } from "@shared/constants";
import { ConfirmDialogResponseEnum, CoordinateFormatEnum } from "@shared/enums";
import { ColumnUtils, GeoJSONUtils } from "@shared/utils";
import DmsCoordinates, { parseDms } from "@shared/utils/dms.utils";
import { CustomValidators } from "@shared/validators";

interface IRowData {
  index: number;
  latitude: number;
  longitude: number;
  isFooter?: boolean;
}

@Component({
  standalone: false,
  selector: "app-coordinates-form",
  templateUrl: "./coordinates-form.component.html",
  styleUrl: "./coordinates-form.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CoordinatesFormComponent implements OnInit, OnDestroy {
  @Input() formGroup!: FormGroup;

  readonly coordinateFormatFormGroup =
    input.required<FormGroup<{ format: FormControl<CoordinateFormatEnum> }>>();

  hasMultipleAreas = input<boolean>(false);

  @ViewChild("grid") public grid: AgGridAngular;

  private api!: GridApi;

  @Output() coordinatesChanged = new EventEmitter<any>();

  public rowData = signal<any[]>([]);

  public columnDefs = [];

  private previousIndex: number | null = null;

  public pinnedBottomRowData: any[] = [
    {
      isFooter: true,
      cellClass: "clickable",
    },
  ];

  private readonly addNewRowText = $localize`:@@shared.addnewrow:Add new row`;

  private readonly pinnedRowHtml = `
  <div class="clickable">
    <span class="pinned-row-btn">
      <span>+</span>
      ${this.addNewRowText}
    </span>
  </div>`;

  private readonly coordinateColumnTooltip = $localize`:@@coordinates-form.typetoaddthecoordinates:Type to add the coordinates`;

  radiusArea = signal<number>(null);

  @Output()
  public removeRadiusArea: EventEmitter<void> = new EventEmitter();

  private readonly fb = inject(FormBuilder);

  private readonly dialog = inject(MatDialog);

  private readonly cdr = inject(ChangeDetectorRef);

  private readonly subscriptions = new Subscription();

  public readonly translations: any = {
    editAreaTp: $localize`:@@shared.editarea:Edit area size`,
    areaHa: TextConstants.AREA_HA,
  };

  private removeClosingCoordinates(rowsArray: FormArray): void {
    for (let i = rowsArray.length - 1; i >= 0; i--) {
      const control = rowsArray.at(i) as FormGroup;

      if (control.get("rowAddedToClosePolygon")?.value === true) {
        rowsArray.removeAt(i);
      }
    }
  }

  public gridOptions: GridOptions = {
    suppressRowTransform: true,
    defaultColDef: {
      width: 137,
      maxWidth: 137,
      sortable: false,
      suppressMovable: true,
      rowDrag: false,
      editable: false,
      suppressSizeToFit: true,
      suppressAutoSize: true,
      cellRenderer: this.renderFooterButton.bind(this),
      lockPinned: true,
      tooltipComponent: TableTooltip,
    },
    suppressHorizontalScroll: true,
    suppressContextMenu: true,
    columnDefs: this.getColumnDefs(),
    domLayout: "autoHeight",
    rowData: this.rowData(),
    singleClickEdit: false,
    enableRangeSelection: false,
    enableCellTextSelection: true,
    copyHeadersToClipboard: true,
    tooltipShowDelay: 100,
    tooltipHideDelay: 3000,
    tooltipTrigger: "focus",
    enableFillHandle: true,
    suppressClipboardPaste: false,
    processDataFromClipboard: this.processDataFromClipboard.bind(this),
    pinnedBottomRowData: this.pinnedBottomRowData,
    rowDragManaged: true,
    stopEditingWhenCellsLoseFocus: true,
  };

  ngOnInit(): void {
    this.columnDefs = this.getColumnDefs();
    const rowData = this.formGroup.value.rows.map((row: any, index: number) => ({
      ...row,
      index: index,
    }));

    this.rowData.set(rowData);
    if (this.getRadiusForm()?.value) {
      const area = GeoJSONUtils.calculateCircleAreaForSinglePoint(
        null,
        +this.getRadiusForm()?.value,
      );
      const areaInHA = GeoJSONUtils.getAreaInHa(area);

      this.radiusArea.set(areaInHA);
    }

    this.subscriptions.add(
      this.coordinateFormatFormGroup()
        .get("format")
        .valueChanges.subscribe(() => {
          TableUtils.redrawRows(this.grid.api);
        }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  onGridReady(params: GridReadyEvent): void {
    this.api = params.api;
    if (!this.grid) {
      return;
    }

    this.grid.api.autoSizeAllColumns();
    this.setGridDomLayout();
  }

  private setGridDomLayout() {
    const maxRowLength = this.hasMultipleAreas() ? 10 : 11;

    if (this.rowData().length > maxRowLength) {
      this.grid.api.setGridOption("domLayout", "normal");
    }
  }

  getColumnDefs(): ColDef<IRowData>[] {
    return [
      {
        ...ColumnUtils.iconColumnCommonValues,
        valueGetter: (params) => (params.node ? params.node.rowIndex + 1 : null),
        width: 70,
        maxWidth: 70,
        rowDrag: (params) => !params.data.isFooter,
        colSpan: (params) => (params.data.isFooter ? 4 : 1),
        cellClass: (params) => `cell-index${params.data.isFooter ? " clickable no-border" : ""}`,
        onCellClicked: (params) => {
          if (params.data.isFooter) {
            this.addRow();
          }
        },
      },
      {
        field: "longitude",
        headerName: $localize`:@@coordinates-form.longx:Long (X)`,
        tooltipValueGetter: () => this.coordinateColumnTooltip,
        editable: (params) => {
          return !params.data.isFooter;
        },
        valueParser: this.onParseValue(GeoJSONUtils.isValidLongitude),
        valueSetter: this.onSetValue("longitude"),
        valueFormatter: (params) => {
          if (
            isNaN(params.value) ||
            params.value === null ||
            this.coordinateFormatFormGroup().get("format").value === CoordinateFormatEnum.DD
          ) {
            return params.value;
          }

          try {
            const dms = new DmsCoordinates(0, params.value);

            return dms.longitude.toString(0);
          } catch {
            return params.value;
          }
        },
        cellRenderer: null,
        cellEditorParams: {
          useFormatter: true,
        },
        cellEditor: "agTextCellEditor",
        onCellValueChanged: this.onCellValueChanged.bind(this),
        cellClass: (params) => {
          if (params.data.isFooter) {
            return "";
          }

          if (params.value && !GeoJSONUtils.isValidLongitude(params.value.toString())) {
            return ["invalid-cell", "coordinates-row"];
          }

          return "coordinates-row";
        },
      } as ColDef<IRowData, number>,
      {
        field: "latitude",
        headerName: $localize`:@@coordinates-form.laty:Lat (Y)`,
        tooltipValueGetter: () => this.coordinateColumnTooltip,
        editable: (params) => {
          return !params.data.isFooter;
        },
        valueParser: this.onParseValue(GeoJSONUtils.isValidLatitude),
        valueSetter: this.onSetValue("latitude"),
        valueFormatter: (params) => {
          if (
            isNaN(params.value) ||
            params.value === null ||
            this.coordinateFormatFormGroup().get("format").value === CoordinateFormatEnum.DD
          ) {
            return params.value;
          }

          try {
            const dms = new DmsCoordinates(params.value, 0);

            return dms.latitude.toString(0);
          } catch {
            return params.value;
          }
        },
        cellRenderer: null,
        cellEditor: "agTextCellEditor",
        cellEditorParams: {
          useFormatter: true,
        },
        onCellValueChanged: this.onCellValueChanged.bind(this),
        cellClass: (params) => {
          if (params.data.isFooter) {
            return "";
          }

          if (params.value && !GeoJSONUtils.isValidLatitude(params.value.toString())) {
            return ["invalid-cell", "coordinates-row"];
          }

          return "coordinates-row";
        },
      } as ColDef<IRowData, number>,
      {
        ...(ColumnUtils.buttons([
          {
            onClick: this.onDelete,
            icon: IconConstants.DELETE,
            tooltip: () =>
              this.rowDataLength > 1
                ? TextConstants.REMOVE
                : $localize`:@@coordinates-form.onepairrequired:You need at least one pair of coordinates`,
            buttonClass: () => (this.rowDataLength > 1 ? "" : "disabled"),
            isVisible: (params: IRowData) => {
              return !params?.isFooter;
            },
          },
        ]) as ColDef<IRowData>),
        width: 65,
        maxWidth: 65,
        cellClass: (params) => {
          return params.data.isFooter ? "no-border" : "no-border cell-index";
        },
      },
    ];
  }

  private onParseValue(validator: (value: string) => boolean) {
    return (params: ValueParserParams) => {
      return this.parseValue(params.newValue, validator);
    };
  }

  private parseValue(value: string, validator: (value: string) => boolean) {
    if (this.coordinateFormatFormGroup().get("format").value === CoordinateFormatEnum.DD) {
      if (isNaN(+value)) {
        return value;
      }

      return +value;
    }

    let parsedValue = parseDms(value);

    if (value && /^[WSEN]/.test(value)) {
      const direction = value[0];
      const restOfValue = value.slice(1).trim();

      parsedValue = parseDms(`${restOfValue}${direction}`);
    }

    const isValid = validator(parsedValue.toString());

    return isNaN(parsedValue) || !isValid ? value : parsedValue;
  }

  private onSetValue(field: string) {
    return (params: ValueSetterParams): boolean => {
      const newValue = isNaN(+params.newValue) ? params.newValue : +params.newValue;
      const rowIndex = params.node.rowIndex;

      const rowsArray = this.formGroup.get("rows") as FormArray;

      rowsArray.at(rowIndex).patchValue({ [field]: newValue });

      params.node.data[field] = newValue;

      return true;
    };
  }

  renderFooterButton(params: any): string {
    if (params.data && params.data.isFooter) {
      if (params.column.getColId() === "0") {
        return this.pinnedRowHtml;
      }

      return "";
    }

    return params.value;
  }

  onDelete = (rowData: { latitude: number; longitude: number; index: number }): void => {
    const rowsArray = this.formGroup.get("rows") as FormArray;

    if (rowsArray.length > 1) {
      this.removeClosingCoordinates(rowsArray);
      rowsArray.removeAt(rowData.index);
      const rows = rowsArray.value.map((row: any, index) => ({
        ...row,
        index: index,
      }));

      this.rowData.set(rows);
      this.emitCoordinatesChangedIfValid({
        affectsFirstRow: rowData.index === 0,
        rowAdded: false,
      });
      this.setGridDomLayout();
    }
  };

  onCellValueChanged(): void {
    const rowsArray = this.formGroup.get("rows") as FormArray;

    this.removeClosingCoordinates(rowsArray);

    const allCoordinatesValid = rowsArray.controls.every((control) => {
      const lat = control.get("latitude").value;
      const lng = control.get("longitude").value;

      return GeoJSONUtils.isValidLatitude(lat) && GeoJSONUtils.isValidLongitude(lng);
    });

    if (allCoordinatesValid) {
      this.emitCoordinatesChangedIfValid({
        affectsFirstRow: false,
        rowAdded: false,
      });
    }
  }

  processDataFromClipboard(params: ProcessDataFromClipboardParams) {
    const rowsArray = this.formGroup.get("rows") as FormArray;

    this.removeClosingCoordinates(rowsArray);

    const emptyLastRow =
      params.data[params.data.length - 1][0] === "" &&
      params.data[params.data.length - 1].length === 1;

    if (emptyLastRow) {
      params.data.splice(params.data.length - 1, 1);
    }

    const lastIndex = this.api.getDisplayedRowCount() - 1;
    const focusedCell = this.api.getFocusedCell();
    const focusedIndex = focusedCell.rowIndex;

    params.data.forEach((rowData, rowIndex) => {
      const targetRowIndex = focusedIndex + rowIndex;

      if (targetRowIndex < rowsArray.length) {
        const rowFormGroup = rowsArray.at(targetRowIndex) as FormGroup;
        let currColumn = focusedCell.column;

        rowData.forEach((cellValue) => {
          const colId = currColumn.getColId();

          let validator = GeoJSONUtils.isValidLongitude.bind(this);

          if (colId === "latitude") {
            validator = GeoJSONUtils.isValidLatitude.bind(this);
          }

          const value = this.parseValue(cellValue, validator);

          rowFormGroup.patchValue({ [colId]: value });
          currColumn = this.api.getDisplayedColAfter(currColumn);
        });
      }
    });

    if (focusedIndex + params.data.length - 1 > lastIndex) {
      const resultLastIndex = focusedIndex + (params.data.length - 1);
      const addRowCount = resultLastIndex - lastIndex;
      let rowsToAdd = [];
      let addedRows = 0;
      let currIndex = params.data.length - 1;

      while (addedRows < addRowCount) {
        rowsToAdd.push(params.data.splice(currIndex, 1)[0]);
        addedRows++;
        currIndex--;
      }

      rowsToAdd = rowsToAdd.reverse();
      const newRowData = [];

      rowsToAdd.forEach((r) => {
        const row = {};
        let currColumn = focusedCell.column;

        r.forEach((i) => {
          const colId = currColumn.getColId();

          row[colId] = i || "";
          currColumn = this.api.getDisplayedColAfter(currColumn);
        });

        newRowData.push(row);
      });

      newRowData.forEach((row) => {
        const latitude = this.parseValue(row.latitude, GeoJSONUtils.isValidLatitude);
        const longitude = this.parseValue(row.longitude, GeoJSONUtils.isValidLongitude);

        const newRow = this.createCoordinateGroup(latitude, longitude);

        rowsArray.push(newRow);
      });

      this.updateRowData();
      this.setGridDomLayout();
    }

    return params.data;
  }

  async addRow() {
    const rowsArray = this.formGroup.get("rows") as FormArray;

    if (rowsArray.length === 1 && this.getRadiusForm()?.value) {
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        data: {
          title: $localize`:@@title.warning:Warning`,
          contentText: $localize`:@@confirm.addingpoint:Adding a point will remove the manually entered area size value, as it will no longer be a single point. The area calculation will be automated based on the shape of the map. Are you sure you want to proceed?`,
        },
      });

      const response = await lastValueFrom(dialogRef.afterClosed());

      if (response === ConfirmDialogResponseEnum.CONFIRM) {
        this.getRadiusForm().setValue(null);
        this.radiusArea.set(null);
        this.removeRadiusArea.emit();
      } else {
        return;
      }
    }

    //Remove the closing point if it matches the first coordinate (indicates a closed polygon)
    if (rowsArray.length >= 4) {
      const first = rowsArray.at(0).value;
      const last = rowsArray.at(rowsArray.length - 1).value;

      const epsilon = 0.000001;
      const isClosure =
        Math.abs(first.latitude - last.latitude) < epsilon &&
        Math.abs(first.longitude - last.longitude) < epsilon;

      if (isClosure) {
        rowsArray.removeAt(rowsArray.length - 1);
      }
    }

    const newRow = this.createCoordinateGroup();

    rowsArray.push(newRow);

    this.updateRowData();
    this.emitCoordinatesChangedIfValid({ affectsFirstRow: false, rowAdded: true });

    setTimeout(() => {
      this.setGridDomLayout();

      const rowCount = this.api.getDisplayedRowCount();

      this.api.ensureIndexVisible(rowCount - 1, "bottom");
    });
  }

  public createCoordinateGroup(
    latitude: number | string = null,
    longitude: number | string = null,
  ): FormGroup {
    return this.fb.group({
      longitude: [longitude, [CustomValidators.longitude, CustomValidators.required]],
      latitude: [latitude, [CustomValidators.latitude, CustomValidators.required]],
    });
  }

  swapColumns() {
    this.api.moveColumnByIndex(1, 2);
  }

  onRowDragEnd(event: RowDragEndEvent) {
    const currentIndex = event.overIndex;
    const originalIndex = event.overNode?.data?.index;

    if (
      this.previousIndex === null ||
      this.previousIndex === currentIndex ||
      currentIndex == null
    ) {
      return;
    }

    const formArray = this.formGroup.get("rows") as FormArray;

    this.removeClosingCoordinates(formArray);

    const movedItem = formArray.at(this.previousIndex);

    formArray.removeAt(this.previousIndex);
    formArray.insert(currentIndex, movedItem);

    this.updateRowData();

    this.emitCoordinatesChangedIfValid({
      affectsFirstRow: originalIndex === 0 || currentIndex === 0,
      rowAdded: false,
    });
    this.previousIndex = null;
  }

  onRowDragEnter(event: RowDragEnterEvent): void {
    this.previousIndex = event.node.rowIndex;
  }

  updateRowData(): void {
    const rowsArray = this.formGroup.get("rows") as FormArray;
    const data = rowsArray.value.map((row: any, index: number) => ({
      ...row,
      index: index,
    }));

    this.rowData.set(data);
    this.grid.rowData = this.rowData();
  }

  private get rowDataLength(): number {
    return this.rowData().length;
  }

  getRadiusForm() {
    return this.formGroup?.get("radius");
  }

  openEditAreaDialog() {
    const hasRadiusForm = !!this.getRadiusForm();
    const dialogRef = this.dialog.open(EditAreaDialogComponent, {
      data: {
        radiusInMeters: hasRadiusForm ? +this.getRadiusForm()?.value : 0,
      },
    });

    dialogRef.afterClosed().subscribe((response) => {
      if (response?.value) {
        if (!hasRadiusForm) {
          this.formGroup.addControl("radius", this.fb.control(null));
        }
        this.getRadiusForm().setValue(response?.value);
        this.radiusArea.set(
          GeoJSONUtils.calculateCircleAreaForSinglePoint(null, response?.value) / 10000,
        );
        this.emitCoordinatesChangedIfValid();
        this.cdr.detectChanges();
      }
    });
  }

  public emitCoordinatesChangedIfValid(
    event: {
      affectsFirstRow: boolean;
      rowAdded: boolean;
    } = null,
  ): void {
    const rowsArray = this.formGroup.get("rows") as FormArray;

    const allRowsValid = rowsArray.controls.every((row: AbstractControl) => row.valid);

    if (allRowsValid) {
      this.coordinatesChanged.emit(event);
    }
  }
}
